import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import ItemsCard from "../../features/MenuItems/components/MenuItemCard";
import closeIcon from "../../assests/images/dashboard/Vector 3.png";
import { PortaledModalFrame } from "src/components/common/PortaledModalFrame";

const ComboItemsModal = ({ title, setShowItemModal, formik }: any) => {
  const [allData, setAllData] = useState<any>([]);
  const [orignalData, setOrignalData] = useState<any>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const isManualUpdate = useRef(false);
  const { menuCategories } = useSelector((state: any) => state.category);

  useEffect(() => {
    const tempData = [...(menuCategories || [])];
    const ids = (formik.values.itemCorrelationIds || []).map(
      (item: any) => item?.correlationId
    );

    const allTempData = tempData[0]?.items
      ? tempData[0].items.map((item: any) => ({
          correlationId: item?.correlationId,
          englishName: item?.englishName,
          priceCents: item?.priceCents,
          taxRate: item?.taxRate,
          tag: item?.tag,
          itemOptions: item?.itemOptions || [],
          itemOptionsCount: item?.itemOptions?.length,
          bucketKeyName: item?.bucketKeyName,
          isSelected: ids?.includes(item.correlationId),
          nonEnglishName: item?.nonEnglishName,
        }))
      : [];
    setOrignalData(tempData);
    setSelectedCategory(tempData?.[0] || {});
    setAllData(allTempData);
  }, [menuCategories]);
  
  // Sync isSelected state when itemCorrelationIds changes (but only if allData already exists and it's not a manual update)
  useEffect(() => {
    if (allData.length === 0 || isManualUpdate.current) {
      isManualUpdate.current = false;
      return;
    }
    const ids = (formik.values.itemCorrelationIds || []).map(
      (item: any) => item?.correlationId
    );
    setAllData((prevData: any[]) => {
      return prevData.map((item: any) => ({
        ...item,
        isSelected: ids?.includes(item.correlationId),
      }));
    });
  }, [formik.values.itemCorrelationIds]);

  const formatItemData = (data: any) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.map((item: any) => ({
      correlationId: item?.correlationId,
      englishName: item?.englishName,
      priceCents: item?.priceCents || item?.price,
      taxRate: item?.taxRate,
      tag: item?.tag,
      itemOptions: item?.itemOptions || [],
      itemOptionsCount: item?.itemOptions?.length,
      bucketKeyName: item?.bucketKeyName,
      isSelected: item?.isSelected,
      nonEnglishName: item?.nonEnglishName,
    }));
  };

  const selectCategory = (item: any) => {
    setSelectedCategory(item);
    const tempData = [...(menuCategories || [])];
    const ids = (formik.values.itemCorrelationIds || []).map(
      (item: any) => item?.correlationId
    );
    const index = tempData.findIndex(
      (data: any) => data.correlationId === item.correlationId
    );
    if (index >= 0 && tempData[index]?.items) {
      const itemsWithSelection = tempData[index].items.map((item: any) => ({
        ...item,
        isSelected: ids?.includes(item.correlationId),
      }));
      const data = formatItemData(itemsWithSelection);
      setAllData(data);
    } else {
      setAllData([]);
    }
  };

  const handleClick = (menuItemIndexes: unknown) => {
    const index = menuItemIndexes as number;
    if (!allData || !allData[index]) {
      return;
    }
    const item = allData[index];
    const correlationId = item.correlationId;
    const isSelected = !item.isSelected;

    // Update formik first
    const currentItems = formik.values.itemCorrelationIds || [];
    const updatedCorrelationItems = isSelected
      ? [
          ...currentItems,
          item,
        ]
      : currentItems.filter(
          (item: any) => item?.correlationId !== correlationId
        );
    
    // Update local state immediately for visual feedback
    setAllData((prevData: any[]) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], isSelected };
      return newData;
    });
    
    // Mark as manual update to prevent useEffect from overwriting
    isManualUpdate.current = true;
    formik.setFieldValue("itemCorrelationIds", updatedCorrelationItems);
  };

  const searchOrders = (search: string) => {
    // Use orignalData if available, otherwise fall back to menuCategories
    const allOrginalData = orignalData.length > 0 
      ? [...orignalData] 
      : [...(menuCategories || [])];
    const ids = (formik.values.itemCorrelationIds || []).map(
      (item: any) => item?.correlationId
    );
    
    if (!search || search.trim() === "") {
      const items = allOrginalData?.[0]?.items || [];
      const itemsWithSelection = items.map((item: any) => ({
        ...item,
        isSelected: ids?.includes(item.correlationId),
      }));
      setAllData(formatItemData(itemsWithSelection));
      setSelectedCategory(allOrginalData?.[0] || {});
      return;
    }
    
    const data = allOrginalData.flatMap((category) => category?.items || []);
    const filteredData = data
      .filter((item) =>
        item?.englishName?.toLowerCase()?.includes(search?.toLowerCase())
      )
      .map((item: any) => ({
        ...item,
        isSelected: ids?.includes(item.correlationId),
      }));
    setAllData(formatItemData(filteredData));
    setSelectedCategory(null);
  };

  const closeModal = () => setShowItemModal(false);

  return (
    <PortaledModalFrame onClose={closeModal} panelStyle={{ maxWidth: 1100 }}>
      <div className="flex min-h-0 max-h-full flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#F1F1F2] px-5 py-4 sm:px-8">
          <span id="modal-title" className="text-xl font-semibold text-[#252525]">
            {title}
          </span>
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100"
            onClick={closeModal}
            aria-label="Close"
          >
            <img className="h-3 w-3" src={closeIcon} alt="" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 py-4 sm:px-8">
              <form className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-x-2">
                <div className="relative w-full min-w-0 sm:w-[240px]">
                  <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-500 dark:text-gray-400"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 20"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                      />
                    </svg>
                  </div>
                  <input
                    type="search"
                    id="default-search"
                    className="block border-[#F1F1F2] border w-full p-2 ps-10 text-sm text-black rounded-lg "
                    placeholder="search by item name"
                    onChange={(e) => searchOrders(e.target.value)}
                  />
                </div>
                <div className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-[#F1F1F2] p-2 sm:ml-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#D1D1D1 #F1F1F2' }}>
                  <style>{`
                    .category-scroll::-webkit-scrollbar {
                      height: 6px;
                      display: block;
                    }
                    .category-scroll::-webkit-scrollbar-track {
                      background: #F1F1F2;
                      border-radius: 3px;
                    }
                    .category-scroll::-webkit-scrollbar-thumb {
                      background: #D1D1D1;
                      border-radius: 3px;
                    }
                    .category-scroll::-webkit-scrollbar-thumb:hover {
                      background: #B1B1B1;
                    }
                  `}</style>
                  <div className="flex gap-x-8 category-scroll">
                    {(menuCategories || []).map((item: any, index: any) => {
                      return (
                        <div
                          key={index}
                          className="flex flex-shrink-0"
                          onClick={() => selectCategory(item)}
                        >
                          <span
                            className={`text-[#939393] font-semibold cursor-pointer border-b-2 whitespace-nowrap max-w-[150px] truncate block ${
                              selectedCategory?.correlationId === item?.correlationId
                                ? "border-[#FD7657]"
                                : "border-none"
                            }`}
                            title={item.name}
                          >
                            {item.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </form>
              <div className="relative z-0 mt-4 min-h-0 w-full flex-1">
                <div
                  className="relative z-0 h-full min-h-0 w-full overflow-y-auto overflow-x-hidden rounded-lg border border-[#F1F1F2] p-4"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: '#D1D1D1 #F1F1F2' }}
                >
                  <style>{`
                    .combo-items-scroll::-webkit-scrollbar {
                      width: 8px;
                      display: block;
                    }
                    .combo-items-scroll::-webkit-scrollbar-track {
                      background: #F1F1F2;
                      border-radius: 4px;
                    }
                    .combo-items-scroll::-webkit-scrollbar-thumb {
                      background: #D1D1D1;
                      border-radius: 4px;
                    }
                    .combo-items-scroll::-webkit-scrollbar-thumb:hover {
                      background: #B1B1B1;
                    }
                  `}</style>
                  <div className="combo-items-scroll w-full h-full">
                    {allData?.length > 0 ? (
                      <div className="grid auto-rows-min grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {allData.map((item: any, index: number) => {
                          return (
                            <div 
                              key={item.correlationId || index} 
                              className="flex justify-center"
                              style={{ position: 'relative' }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 1,
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleClick(index);
                                }}
                              />
                              <ItemsCard
                                menuItem={item}
                                isSelected={item.isSelected}
                                isCombo={true}
                                handleMenuItemClick={() => {}} // Not used for combo selection
                                handleClick={(menuItemIndexes) => {
                                  const idx = menuItemIndexes as number;
                                  handleClick(idx);
                                }}
                                menuItemIndexes={index}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p>Create some items to add combo!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
        </div>
        <div className="flex shrink-0 justify-end border-t border-[#F1F1F2] px-5 py-4 sm:px-8">
          <button
            type="button"
            className="flex h-9 items-center justify-center gap-2 rounded-md bg-orange px-8 text-xs text-white hover:bg-orange/90"
            onClick={() => setShowItemModal()}
          >
            Add to Combo
          </button>
        </div>
      </div>
    </PortaledModalFrame>
  );
};

export default ComboItemsModal;
